import React, {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import {useCart, cartKey} from "../context/CartContext";
import {getProducts} from "../api";
import {calcPricing, nextOfferHint} from "../utils/pricing";
import "./CartDrawer.css";

export default function CartDrawer() {
  const {
    cart,
    drawerOpen,
    setDrawerOpen,
    removeFromCart,
    setQty,
    subtotal,
    addToCart,
  } = useCart();
  const navigate = useNavigate();

  const [suggestions, setSuggestions] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const category = searchParams.get("category") || "";
  const sort = searchParams.get("sort") || "-createdAt";
  const bestseller = searchParams.get("bestseller") || "";
  const page = Number(searchParams.get("page") || 1);

  // Load "You might also like" products the first time the drawer is opened
  useEffect(() => {
    getProducts()
      .then((r) => {
        setSuggestions(r.data.products || []);
      })
      .catch(() => {});
  }, [drawerOpen, suggestions.length]);

  if (!drawerOpen) return null;

  const cartIds = new Set(cart.map((i) => i._id));
  const recommended = suggestions
    .filter((p) => !cartIds.has(p._id))
    .slice(0, 5);

  const {shippingFee, discount, freeGift, total} = calcPricing(subtotal);
  const offerHint = nextOfferHint(subtotal);

  return (
    <>
      {/* Backdrop */}
      <div className="cd-backdrop" onClick={() => setDrawerOpen(false)} />

      {/* Drawer */}
      <div className="cd-drawer">
        {/* Header */}
        <div className="cd-header">
          <h3 style={{color: "white"}}>
            🛒 Your Cart{" "}
            <span className="cd-count">
              ({cart.reduce((s, i) => s + i.qty, 0)})
            </span>
          </h3>
          <button className="cd-close" onClick={() => setDrawerOpen(false)}>
            ✕
          </button>
        </div>

        {/* Items */}
        <div className="cd-items">
          {cart.length === 0 ? (
            <div className="cd-empty">
              <span>🛒</span>
              <p>Your cart is empty</p>
            </div>
          ) : (
            cart.map((item) => (
              <div key={cartKey(item)} className="cd-item">
                <img
                  src={
                    item.images?.[0]?.url ||
                    item.images?.[0] ||
                    "https://placehold.co/60x60/f5f5f5/aaa?text="
                  }
                  alt={item.name}
                  className="cd-item-img"
                />
                <div className="cd-item-info">
                  <p className="cd-item-name">{item.name}</p>
                  {item.customName && (
                    <p className="cd-item-custom">Name: {item.customName}</p>
                  )}
                  <p className="cd-item-price">
                    ₹{item.price.toLocaleString("en-IN")}
                  </p>
                  {item.customName ? (
                    <div className="cd-qty">
                      <button onClick={() => removeFromCart(cartKey(item))}>
                        🗑️
                      </button>
                    </div>
                  ) : (
                  <div className="cd-qty">
                    <button
                      onClick={() =>
                        item.qty === 1
                          ? removeFromCart(cartKey(item))
                          : setQty(cartKey(item), item.qty - 1)
                      }
                    >
                      {item.qty === 1 ? "🗑️" : "−"}
                    </button>
                    <span>{item.qty}</span>
                    <button
                      onClick={() => setQty(cartKey(item), item.qty + 1)}
                    >
                      +
                    </button>
                  </div>
                  )}
                </div>
                <span className="cd-item-total">
                  ₹{(item.price * item.qty).toLocaleString("en-IN")}
                </span>
              </div>
            ))
          )}

          {/* You might also like */}
          {cart.length > 0 && recommended.length > 0 && (
            <div className="cd-suggest">
              <p className="cd-suggest-title">You might also like</p>
              {recommended.map((p) => (
                <div key={p._id} className="cd-suggest-item">
                  <img
                    src={
                      p.images?.[0]?.url ||
                      p.images?.[0] ||
                      "https://placehold.co/60x60/f5f5f5/aaa?text="
                    }
                    alt={p.name}
                    className="cd-item-img"
                  />
                  <div className="cd-item-info">
                    <p className="cd-item-name">{p.name}</p>
                    <p className="cd-item-price">
                      ₹{p.price.toLocaleString("en-IN")}
                    </p>
                  </div>
                  <button
                    className="cd-suggest-add"
                    onClick={() => {
                      if (p.isCustomizable) {
                        setDrawerOpen(false);
                        navigate(`/products/${p.slug || p._id}`);
                      } else addToCart(p);
                    }}
                  >
                    {p.isCustomizable ? "Personalise" : "Add"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="cd-footer">
            <div className="cd-subtotal">
              <span>Subtotal</span>
              <span>₹{subtotal.toLocaleString("en-IN")}</span>
            </div>
            <div className="cd-shipping">
              <span>Shipping</span>
              <span>
                {shippingFee === 0 ? (
                  <span className="cd-free">FREE</span>
                ) : (
                  `₹${shippingFee}`
                )}
              </span>
            </div>
            {discount > 0 && (
              <div className="cd-shipping">
                <span>Discount (10% off)</span>
                <span className="cd-free">
                  −₹{discount.toLocaleString("en-IN")}
                </span>
              </div>
            )}
            {freeGift && (
              <div className="cd-shipping">
                <span>🎁 Surprise gift</span>
                <span className="cd-free">FREE</span>
              </div>
            )}
            {offerHint && <p className="cd-free-hint">{offerHint}</p>}
            <div className="cd-total">
              <span>Total</span>
              <span>₹{total.toLocaleString("en-IN")}</span>
            </div>

            <button
              className="cd-checkout-btn"
              onClick={() => {
                setDrawerOpen(false);
                navigate("/checkout");
              }}
            >
              Checkout →
            </button>
            <button
              className="cd-view-btn"
              onClick={() => {
                setDrawerOpen(false);
                navigate("/cart");
              }}
            >
              View Full Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
}
